import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Colors from '../../utils/color';
import { useAppSelector } from '../../app/hooks';
import { RootState } from '../../app/store';
import useOrderCheckRetry from '../../hooks/useOrderCheckRetry.hooks';
import {
  OrderPipelineState,
  selectMenuModuleCartErrors,
  selectOrderError,
  selectOrderPipeline,
} from '../../selectors/order';
import {
  selectCartItems,
  selectHypothesisCartItems,
} from '../../selectors/cart';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';

/**
 * Persistent cart-top strip showing the order pipeline's current state
 * (HITL-1527): appears when the session's first order event goes out and stays
 * until the session ends, for agent and AI orders, non-final and final. A
 * full-bleed bar, not a card — square corners, edge to edge — so it never
 * reads as another cart item. Hidden when the cart has been emptied (unless a
 * final order is being tracked — the finish flow empties the cart too).
 *
 * Compact single-row layout (demo feedback): the strip's BACKGROUND says who
 * submitted the order — AI orders reuse the AI-added cart item color
 * (Colors.aiItemIndicator) and agent orders the normal cart item white, so the
 * banner reads like the cart lines it sits above (no origin badge). The
 * message carries the words, and a three-segment stage RING on the right
 * carries the state: the segments are Sent, Acknowledged, Confirmed
 * (clockwise from the top), filled up to the stage the round trip reached —
 * green while healthy, orange when stuck, red on failure (still showing the
 * reached stage), with a check or cross marking the terminal states. Static,
 * never spinning. The live elapsed counter (1-indexed, capped at 99) renders
 * INSIDE the ring while the order is in flight. When the order is STUCK the
 * ring itself becomes the Retry button: the counter keeps the center (same as
 * every other waiting state) and a small orange corner badge with a replay
 * glyph is the click affordance — the whole 26px ring is the click target.
 * No info icon, no stage labels — height is a single line except when the
 * menu-module error list adds one.
 *
 * The pipeline state comes from selectOrderPipeline — the single derivation —
 * and the stuck/retry behavior (overdue clock, auto-resend countdown,
 * takeover click) is exactly useOrderCheckRetry's, unchanged. The plain Retry
 * alert (OrderCheckRetryAlert) renders instead when the order_status_banner
 * flag is off — see OrderReview.
 */

// Live seconds since `sentAt`, ticking only while `active`. Computed during
// render (the interval only forces re-renders) so a new order's counter is
// correct on its very first frame. 1-indexed: a fresh order reads (1s).
// The display clamps at ELAPSED_CAP_SECONDS, so the tick stops there too —
// a stuck order (e.g. a final the POS never answers) must not keep a 1 Hz
// re-render running all session behind a number frozen at the cap.
const ELAPSED_CAP_SECONDS = 99;

const useElapsedSeconds = (sentAt: string | undefined, active: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sentAt || !active) {
      return undefined;
    }
    const sentAtMs = new Date(sentAt).getTime();
    const intervalId = setInterval(() => {
      if ((Date.now() - sentAtMs) / 1000 >= ELAPSED_CAP_SECONDS) {
        clearInterval(intervalId); // One last render shows the capped value.
      }
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [sentAt, active]);
  if (!sentAt || !active) {
    return null;
  }
  return Math.max(
    1,
    Math.ceil((Date.now() - new Date(sentAt).getTime()) / 1000)
  );
};

type RingTone = 'ok' | 'warning' | 'error';
type RingMark = 'none' | 'check' | 'cross';

const RING_COLORS: Record<RingTone, string> = {
  ok: Colors.apple,
  warning: Colors.zest,
  error: Colors.amaranth,
};
const RING_TRACK = Colors.botticelli;
// Tinted counter matching the tone, dark enough to read at 9px.
const RING_TEXT: Record<RingTone, string> = {
  ok: Colors.dell,
  warning: Colors.chelseaGem,
  error: Colors.mexicanRed,
};

// r=10.5 (26px box — the minimum that fits a two-digit counter inside) →
// circumference ≈ 65.97, split into three equal arcs with small gaps:
// 1st = Sent, 2nd = Acknowledged, 3rd = Confirmed (clockwise from the top).
const CIRCUMFERENCE = 2 * Math.PI * 10.5;
const SEGMENT_GAP = 3;
const SEGMENT_LENGTH = CIRCUMFERENCE / 3 - SEGMENT_GAP;

function StageRing({
  stage,
  tone,
  mark,
  seconds,
}: {
  stage: 1 | 2 | 3; // how many segments are filled
  tone: RingTone;
  mark: RingMark;
  // Elapsed seconds shown INSIDE the ring while a counter is meaningful; the
  // check/cross replaces it in terminal states. Capped at 99 for fit.
  seconds: number | null;
}) {
  const arc = RING_COLORS[tone];
  const showSeconds = seconds != null && mark === 'none';
  return (
    <Box
      component="svg"
      width={26}
      height={26}
      viewBox="0 0 26 26"
      data-testid="order-status-ring"
      data-stage={stage}
      data-tone={tone}
      data-mark={mark}
      data-seconds={
        showSeconds ? Math.min(ELAPSED_CAP_SECONDS, seconds) : undefined
      }
      aria-hidden="true"
      sx={{ flex: 'none' }}
    >
      {[0, 1, 2].map((i) => (
        <circle
          key={i}
          cx="13"
          cy="13"
          r="10.5"
          fill="none"
          stroke={i < stage ? arc : RING_TRACK}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${SEGMENT_LENGTH} ${
            CIRCUMFERENCE - SEGMENT_LENGTH
          }`}
          strokeDashoffset={-(i * CIRCUMFERENCE) / 3 - SEGMENT_GAP / 2}
          transform="rotate(-90 13 13)"
        />
      ))}
      {showSeconds && (
        <text
          x="13"
          y="13.5"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="9"
          fontWeight="500"
          fill={RING_TEXT[tone]}
        >
          {Math.min(ELAPSED_CAP_SECONDS, seconds)}
        </text>
      )}
      {mark === 'check' && (
        <path
          d="M9 13.3l2.8 2.8 5.6-5.8"
          fill="none"
          stroke={Colors.dell}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      )}
      {mark === 'cross' && (
        <path
          d="M9.5 9.5l7 7M16.5 9.5l-7 7"
          fill="none"
          stroke={Colors.mexicanRed}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      )}
    </Box>
  );
}

export default function OrderStatusBanner({
  isUnifiedMenu,
  menuUnificationOrderFailureCallback,
  menuUnificationOrderSuccessCallback,
}: {
  isUnifiedMenu: boolean;
  menuUnificationOrderFailureCallback: Function;
  menuUnificationOrderSuccessCallback: Function;
}) {
  const pipeline = useAppSelector(selectOrderPipeline);
  const cartErrors = useAppSelector(selectMenuModuleCartErrors);
  const orderError = useAppSelector(selectOrderError);
  const cartItems = useAppSelector(selectCartItems);
  const hypothesisCartItems = useAppSelector(selectHypothesisCartItems);
  const isHitl3 = useAppSelector(
    (state: RootState) => state.config.FEATURE_FLAG_HITL_3_0
  );
  const {
    isCheckOverdue,
    canRetry,
    isRetrying,
    autoRetrySecondsRemaining,
    retry,
  } = useOrderCheckRetry({
    isUnifiedMenu,
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  });

  const isAwaitingPos =
    pipeline?.state === OrderPipelineState.sending ||
    pipeline?.state === OrderPipelineState.acked;
  const elapsedSeconds = useElapsedSeconds(
    pipeline?.sentAt,
    isAwaitingPos || isCheckOverdue
  );

  // Dismissal for the send-failed error LIST (review feedback: the old
  // OrderErrorSummary could be closed; this strip is sticky, so a multi-item
  // list would otherwise pin over the exact cart lines the agent must edit).
  // Keyed on the error set's fingerprint, so a NEW set of errors re-opens the
  // list. Only the list collapses — the one-line "Order not sent" message is
  // load-bearing and stays.
  const [dismissedErrorsKey, setDismissedErrorsKey] = useState<string | null>(
    null
  );

  if (!pipeline) {
    return null; // No order sent this session yet.
  }

  // QA: deleting every item leaves the banner narrating an order the cart no
  // longer represents — hide it once the cart (real + hypothesis items) is
  // empty. Finals are exempt: the finish-order flow empties the cart in the
  // same breath, and tracking the final through to its check is the banner's
  // job.
  const isCartEmpty =
    Object.keys(cartItems || {}).length === 0 &&
    Object.keys(hypothesisCartItems || {}).length === 0;
  if (isCartEmpty && !pipeline.isFinal) {
    return null;
  }

  const orderNoun = pipeline.isFinal ? 'final order' : 'order';
  const orderTitle = pipeline.isFinal ? 'Final order' : 'Order';

  let message: React.ReactNode;
  let tone: RingTone;
  let mark: RingMark = 'none';
  let showRetry = false;

  if (pipeline.state === OrderPipelineState.sendFailed) {
    // Outranks the stuck warning: a Retry would re-fail at the menu module
    // until the offending items are fixed. The order-level error list
    // (formerly the separate OrderErrorSummary alert, HITL-1457) renders in
    // this strip; the per-line red alerts stay on the cart lines — they
    // locate the item and carry the click-to-send customer TTS.
    tone = 'error';
    mark = 'cross';
    message = 'Order not sent — fix the errors below';
  } else if (isCheckOverdue) {
    tone = 'warning';
    // Manual-only stuck (AI finals, AI driving, or the episode's auto-send is
    // spent) names the control explicitly — QA: agents didn't realize the
    // ring was asking to be clicked. The replay symbol is an inline SVG (same
    // path as the ring's corner badge), not a font glyph: the ⟳ character
    // came from a fallback symbol font whose baseline never lined up with the
    // message text. A stuck order with NO retry (a HITL-clicked final — its
    // cart is already cleared, so a resend would send an empty final) states
    // the fact plainly, promising nothing.
    message = !canRetry ? (
      `${orderTitle} unconfirmed`
    ) : autoRetrySecondsRemaining != null ? (
      `${orderTitle} unconfirmed — resending in ${autoRetrySecondsRemaining}s`
    ) : (
      <>
        {orderTitle} unconfirmed — click{' '}
        <Box
          component="svg"
          width={15}
          height={15}
          viewBox="0 0 24 24"
          aria-hidden="true"
          sx={{ verticalAlign: '-2px' }}
        >
          <path
            d="M4 12a8 8 0 1 0 2.3-5.7"
            fill="none"
            stroke={Colors.christine}
            strokeWidth="3"
          />
          <path
            d="M6 3v6h6"
            fill="none"
            stroke={Colors.christine}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </Box>{' '}
        to resend
      </>
    );
    showRetry = canRetry;
  } else {
    switch (pipeline.state) {
      case OrderPipelineState.disconnected:
        tone = 'error';
        mark = 'cross';
        message = 'POS disconnected — resends on reconnect';
        break;
      case OrderPipelineState.rejected:
        tone = 'error';
        mark = 'cross';
        message = 'POS rejected — final order already received';
        break;
      case OrderPipelineState.failed:
        tone = 'error';
        mark = 'cross';
        message = `POS error on the ${orderNoun}`;
        break;
      case OrderPipelineState.confirmed:
        tone = 'ok';
        mark = 'check';
        message = `${orderTitle} confirmed${
          pipeline.total ? ` · $${pipeline.total}` : ''
        }`;
        break;
      case OrderPipelineState.acked:
        tone = 'ok';
        message = 'POS acknowledged — awaiting check';
        break;
      case OrderPipelineState.sending:
      default:
        tone = 'ok';
        message = `${orderTitle} sent — awaiting POS`;
        break;
    }
  }

  const isAI = pipeline.origin === 'AI';
  const showPosError =
    (pipeline.state === OrderPipelineState.failed ||
      pipeline.state === OrderPipelineState.rejected) &&
    orderError;

  // Fingerprint of the current error set — the dismiss key. A different set
  // (new rejection, extra item) mints a new key, so the list re-opens.
  const errorsKey = cartErrors
    .map((e) => `${e.reasonCode}:${e.nodePath.join('>')}:${e.message}`)
    .join('|');
  const showErrorList =
    pipeline.state === OrderPipelineState.sendFailed &&
    errorsKey !== dismissedErrorsKey;

  return (
    <Box
      role="status"
      data-origin={isAI ? 'AI' : 'AGENT'}
      // Pinned like the Retry alert it replaces: visible on long carts, items
      // scroll underneath (HITL-1483). The background says who submitted the
      // order — the same colors the cart lines below use (AI-added items vs
      // normal items) — so it stays constant across pipeline states; the ring
      // and copy carry the state.
      //
      // Full-bleed bar, not a card (QA: an inset rounded strip in the item
      // colors read as another cart item): square corners, edge-to-edge, only
      // a hairline divider below. The negative margins cancel the wrapping
      // scroll area's inset — HITL 3.0's AgentCartCard itemsScroll pads
      // `0 ${HITL3_PADDING}`, legacy RightBar's subContainer pads 10px all
      // around (top included, hence the negative top there).
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        margin: isHitl3 ? `0 -${HITL3_PADDING} 8px` : '-10px -10px 8px',
        padding: '7px 14px',
        backgroundColor: isAI ? Colors.aiItemIndicator : Colors.white,
        borderBottom: `1px solid ${isAI ? Colors.athsSpecial : Colors.geyser}`,
        color: Colors.elephant,
        fontSize: isHitl3 ? HITL3_FONT_SIZE : '14px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box component="span" sx={{ flex: 1, lineHeight: 1.45 }}>
          {message}
        </Box>
        {showRetry ? (
          // Stuck: the ring IS the Retry button — one 26px control. The
          // counter keeps the ring's center (consistent with every other
          // waiting state); the orange corner badge with the replay glyph is
          // the click affordance.
          <Tooltip title="Resend the order now">
            <span>
              <IconButton
                aria-label="Retry"
                size="small"
                onClick={retry}
                disabled={isRetrying}
                sx={{
                  position: 'relative',
                  overflow: 'visible',
                  width: 26,
                  height: 26,
                  padding: 0,
                  '&.Mui-disabled': { opacity: 0.4 },
                }}
              >
                <StageRing
                  stage={pipeline.stage}
                  tone={tone}
                  mark="none"
                  seconds={elapsedSeconds}
                />
                <Box
                  component="span"
                  data-testid="order-retry-badge"
                  sx={{
                    position: 'absolute',
                    right: -4,
                    bottom: -4,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: Colors.christine,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="3"
                    aria-hidden="true"
                  >
                    <path d="M4 12a8 8 0 1 0 2.3-5.7" />
                    <path d="M6 3v6h6" />
                  </svg>
                </Box>
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <StageRing
            stage={pipeline.stage}
            tone={tone}
            mark={mark}
            seconds={elapsedSeconds}
          />
        )}
        {showErrorList && (
          <Tooltip title="Dismiss error details">
            <IconButton
              aria-label="Dismiss error details"
              size="small"
              onClick={() => setDismissedErrorsKey(errorsKey)}
              sx={{ width: 26, height: 26, color: Colors.elephant }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {showErrorList && (
        // No origin badge anymore — the message starts at the strip's padding,
        // so the list aligns flush under it.
        <Box
          component="ul"
          sx={{
            margin: '2px 0 0 0',
            padding: 0,
            listStylePosition: 'inside',
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        >
          {cartErrors.map((cartError) => (
            <li key={cartError.nodePath.join(' > ') || cartError.message}>
              {cartError.nodePath.length > 0
                ? `${cartError.reasonCode}: ${cartError.nodePath.join(' › ')}`
                : cartError.message}
            </li>
          ))}
        </Box>
      )}
      {showPosError && (
        <Box sx={{ marginTop: '2px', fontSize: '12.5px' }}>{orderError}</Box>
      )}
    </Box>
  );
}
